import { useState } from "react";
import { useQuery, useMutation, useQueryClient } from "@tanstack/react-query";
import { Button } from "@/components/ui/button";
import { Badge } from "@/components/ui/badge";
import { Skeleton } from "@/components/ui/skeleton";
import { Alert, AlertDescription } from "@/components/ui/alert";
import {
  getLeaderboardMeta,
  pauseLeaderboard,
  resumeLeaderboard,
  newLeaderboardSeason,
} from "@/lib/api";

// Only one board is live today (README.md, "The live surface is 3 handlers,
// not 7 and not 4") — EC2Constant.CHAMBER_OF_TRIAL, i.e. "CoT_3.0". This
// stays a plain constant rather than a picker until a second board exists.
const BOARD = "CoT_3.0";
const BOARD_LABEL = "Chamber of Trial";

export function LeaderboardPanel() {
  const qc = useQueryClient();
  const [confirmingSeason, setConfirmingSeason] = useState(false);

  const query = useQuery({
    queryKey: ["leaderboard-meta", BOARD],
    queryFn: () => getLeaderboardMeta(BOARD),
  });

  const invalidate = () => qc.invalidateQueries({ queryKey: ["leaderboard-meta", BOARD] });

  const pause = useMutation({
    mutationFn: () => pauseLeaderboard(BOARD),
    onSuccess: invalidate,
  });
  const resume = useMutation({
    mutationFn: () => resumeLeaderboard(BOARD),
    onSuccess: invalidate,
  });
  const newSeason = useMutation({
    mutationFn: () => newLeaderboardSeason(BOARD),
    onSuccess: () => {
      setConfirmingSeason(false);
      invalidate();
    },
  });

  const meta = query.data;

  return (
    <>
      <div className="space-y-6 max-w-2xl">
        <p className="text-sm text-muted-foreground">Manage submissions and seasons for the Chamber of Trial leaderboard.</p>
        <div className="rounded-lg border border-border bg-card p-4 space-y-4">
          <div className="flex items-center justify-between">
            <div>
              <h3 className="text-sm font-medium">{BOARD_LABEL}</h3>
              <p className="text-xs text-muted-foreground font-mono">{BOARD}</p>
            </div>
            {!meta ? null : meta.submissionPaused ? (
              <Badge variant="destructive">Submissions paused</Badge>
            ) : (
              <Badge variant="secondary">Accepting submissions</Badge>
            )}
          </div>

          {query.isLoading ? (
            <Skeleton className="h-16 w-full" />
          ) : meta ? (
            <div className="grid grid-cols-2 gap-4 text-sm">
              <div>
                <div className="text-xs text-muted-foreground">Current version</div>
                <div className="font-mono">{meta?.version}</div>
              </div>
              <div>
                <div className="text-xs text-muted-foreground">Entries this season</div>
                <div className="font-mono">{meta?.entryCount}</div>
              </div>
            </div>
          ) : null}

          {query.isError && (
            <Alert variant="destructive">
              <AlertDescription>Could not load leaderboard status: {String(query.error)}</AlertDescription>
            </Alert>
          )}

          {meta && <div className="flex items-center gap-2 pt-2 border-t border-border/60">
            {meta?.submissionPaused ? (
              <Button size="sm" onClick={() => resume.mutate()} disabled={resume.isPending}>
                {resume.isPending ? "Resuming…" : "Resume submissions"}
              </Button>
            ) : (
              <Button
                size="sm"
                variant="outline"
                onClick={() => pause.mutate()}
                disabled={pause.isPending}
              >
                {pause.isPending ? "Pausing…" : "Pause submissions"}
              </Button>
            )}
          </div>}
          {(pause.error || resume.error) && (
            <Alert variant="destructive">
              <AlertDescription>{String(pause.error ?? resume.error)}</AlertDescription>
            </Alert>
          )}
        </div>

        <div className="rounded-lg border border-destructive/40 bg-card p-4 space-y-3">
          <h3 className="text-sm font-medium">New season</h3>
          <p className="text-xs text-muted-foreground">
            Bumps the board version. Every existing score becomes invisible to players at once —
            the old season stays in the database (queryable for support questions) but no longer
            shows on the live board. This cannot be undone from here.
          </p>
          {!confirmingSeason ? (
            <Button size="sm" variant="destructive" onClick={() => setConfirmingSeason(true)} disabled={!meta}>
              Start new season…
            </Button>
          ) : (
            <div className="flex items-center gap-2">
              <Button
                size="sm"
                variant="destructive"
                onClick={() => newSeason.mutate()}
                disabled={!meta || newSeason.isPending}
              >
                {newSeason.isPending
                  ? "Starting…"
                  : `Confirm: v${meta?.version ?? "?"} → v${(meta?.version ?? 0) + 1}`}
              </Button>
              <Button size="sm" variant="ghost" onClick={() => setConfirmingSeason(false)}>
                Cancel
              </Button>
            </div>
          )}
          {newSeason.error && (
            <Alert variant="destructive">
              <AlertDescription>{String(newSeason.error)}</AlertDescription>
            </Alert>
          )}
        </div>
      </div>
    </>
  );
}
